import { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { Box, Fade, Grid, Stack, TextField, Typography, useMediaQuery, useTheme } from '@mui/material';
import { AnswerComponent } from './Answer';

import { styles } from './multipleChoiceQuestion.style';
import { useInvestigationDetails } from '@hooks/useInvestigationDetails';
import { LocalizationContext } from '@context/LocalizationContext';
import { useAnalytics } from '@hooks/useAnalytics';
import { REFINE_ANALYTICS_EVENTS } from '@/types/RefineAnalyticsEvents';
import { ExclamationMarkIconSmall } from '@icons/ExclamationMarkIconSmall';
import { refineFonts } from '@/Theming/refineFonts';

interface IMultipleChoiceQuestionsProps {
	questionId: string;
	setCurrentAnswer: (answerId: any, answerText?: any) => void;
	isTextInputInvalid: boolean;
}

export default function MultipleChoiceQuestionComponent(props: IMultipleChoiceQuestionsProps) {
	const investigationDetails = useInvestigationDetails();
	const [selectedAnswerId, setSelectedAnswerId] = useState<string | null>(null);
	const [openAnswer, setOpenAnswer] = useState<string | null>(null);
	const scrollTargetRef = useRef<any>(null);
	const theme = useTheme();
	const [isInputFocused, setIsInputFocused] = useState(false);
	const isTextInputError = props.isTextInputInvalid && !isInputFocused;
	const secondaryTextColor = theme.palette.typographyText.textSecondary;

	const isOUCFQuestion = investigationDetails?.investigationType === 'oucf';

	const { trackRefineEvent } = useAnalytics();

	const isAnswerOpen = (answerId: string | null): boolean => {
		if (answerId && investigationDetails && investigationDetails.nextQuestion) {
			if (investigationDetails.nextQuestion.possibleAnswers[answerId]) {
				const isAnswerOpen = investigationDetails.nextQuestion.possibleAnswers[answerId].isOpen;
				if (isAnswerOpen) {
					trackRefineEvent(REFINE_ANALYTICS_EVENTS.OPEN_ANSWER_CLICKED);
				}
				return isAnswerOpen;
			}
		}

		return false;
	};

	const isOpenAnswer = useMemo(() => isAnswerOpen(selectedAnswerId), [selectedAnswerId]);

	const { Answer: Text, A11Y, CheckQuestion } = useContext(LocalizationContext);

	const possibleAnswers: string[] = useMemo(() => {
		if (investigationDetails && investigationDetails.nextQuestion) {
			return Object.keys(investigationDetails.nextQuestion.possibleAnswers);
		}

		return [];
	}, [investigationDetails]);

	useEffect(() => {
		setSelectedAnswerId(null);
		setOpenAnswer(null);
	}, [investigationDetails]);

	function setSelectedAnswer(answerId: string, answerText: string) {
		setSelectedAnswerId(answerId);
		const question = investigationDetails?.nextQuestion;
		let openText: string | null = '';
		if (question && question.possibleAnswers) {
			if (question.possibleAnswers[answerId].isOpen) {
				openText = openAnswer;
			} else {
				openText = '';
			}
		}
		props.setCurrentAnswer(answerId, openText);
	}

	useEffect(() => {
		setOpenAnswer(null);
		props.setCurrentAnswer(selectedAnswerId, null);
	}, [selectedAnswerId]);

	useEffect(() => {
		if (scrollTargetRef.current && isOpenAnswer) {
			scrollTargetRef.current.scrollIntoView({ behavior: 'smooth' });
		}
	}, [selectedAnswerId]);

	const [gridTemplate, setGridTemplate] = useState<string>('1fr 1fr 1fr 1fr');

	const greaterThanMid = useMediaQuery(theme.breakpoints.up('md'));
	const smallToMid = useMediaQuery(theme.breakpoints.between('sm', 'md'));
	const lessThanSmall = useMediaQuery(theme.breakpoints.down('sm'));
	// if (greaterThanMid) {
	// 	console.log('Greater than mid');
	// } else if (smallToMid) {
	// 	console.log('Between small to mid.');
	// } else if (lessThanSmall) {
	// 	console.log('Less than small');
	// }

	const answerContainerRef = useRef<any>(null);

	const textInputWidth = useMemo(() => {
		const answerWidth = answerContainerRef.current?.getBoundingClientRect().width;
		const gridParts = gridTemplate?.split(' ');
		const partsCount = gridParts.filter((part) => part === '1fr').length;
		//If we don't have the answer width, we set the width to 100% to avoid layout shift
		if (!answerWidth) {
			return '100%';
		}
		return `${answerWidth * partsCount}px`;
	}, [answerContainerRef.current, gridTemplate]);

	useEffect(() => {
		const possibleAnswersCount = Object.keys(investigationDetails?.nextQuestion.possibleAnswers).length;
		if (possibleAnswersCount === 6) {
			if (greaterThanMid) setGridTemplate('1fr 1fr 1fr');
			if (smallToMid) setGridTemplate('1fr 1fr 1fr');
			if (lessThanSmall) setGridTemplate('1fr 1fr');
		}

		if (possibleAnswersCount === 4) {
			if (lessThanSmall) setGridTemplate('1fr 1fr');
			if (smallToMid) setGridTemplate('1fr 1fr');
			if (greaterThanMid) setGridTemplate('1fr 1fr 1fr 1fr');
		}

		if (possibleAnswersCount === 2) {
			if (lessThanSmall) setGridTemplate('1fr 1fr');
			if (smallToMid) setGridTemplate('1fr 1fr');
			if (greaterThanMid) setGridTemplate('1fr 1fr');
		}
		if (possibleAnswersCount === 1) {
			if (lessThanSmall) setGridTemplate('1fr');
			if (smallToMid) setGridTemplate('1fr');
			if (greaterThanMid) setGridTemplate('1fr');
		}
	}, [lessThanSmall, greaterThanMid, smallToMid, investigationDetails, props.questionId]);

	const inputLabelText = isOUCFQuestion
		? CheckQuestion.TextInputLabel || 'Describe what appears wrong or unfamiliar about the check'
		: (Text.PleaseDescribe ?? 'Please Describe');

	if (investigationDetails?.nextQuestion == null) return <></>;
	return (
		<Stack
			overflow={'scroll'}
			flexGrow={1}
			maxWidth={'100%'}
			gap={'24px'}
			justifyContent={'start'}
			alignItems={'center'}
			justifyItems={'start'}>
			<Typography
				variant={'questionText'}
				textAlign={'center'}
				data-cy={'pqMultipleQuestionText'}
				tabIndex={0}
				aria-label={`${A11Y.TheQuestionIs} ${investigationDetails?.nextQuestion.questionText}`}>
				{investigationDetails?.nextQuestion.questionText}
			</Typography>

			<Box
				width={'40%'}
				sx={{
					display: 'grid',
					gridTemplateColumns: gridTemplate,
					gridRow: 2,
					gap: '8px',
					justifyContent: 'center',
				}}>
				{possibleAnswers.map((index) => {
					return (
						<AnswerComponent
							ref={answerContainerRef}
							key={index}
							index={parseInt(index)}
							disabled={false}
							setAnswer={setSelectedAnswer}
							selectedAnswerId={selectedAnswerId}
							text={investigationDetails?.nextQuestion.possibleAnswers[index].text}
							icon={investigationDetails?.nextQuestion.possibleAnswers[index].icon ?? ''}
							answerId={investigationDetails?.nextQuestion.possibleAnswers[index].answerId}
						/>
					);
				})}
			</Box>

			<Fade in={isOpenAnswer} style={{ transitionDelay: '20ms' }}>
				<Stack
					width={'100%'}
					maxWidth={'650px'}
					alignItems={'center'}
					justifyContent={'center'}
					sx={styles.openAnswerContainer}>
					<TextField
						data-cy={'openAnswerTextInput'}
						onFocus={() => setIsInputFocused(true)}
						onBlur={() => setIsInputFocused(false)}
						error={isTextInputError}
						value={openAnswer ?? ''}
						variant={'outlined'}
						slotProps={{
							inputLabel: {
								shrink: false,
							},
							formHelperText: {
								component: 'div',
							},
						}}
						sx={{
							width: textInputWidth,
							'.MuiFormHelperText-root': {
								marginLeft: '0',
							},
							'.MuiFormLabel-root': {
								color: `${secondaryTextColor} !important`,
								opacity: 1,
							},
						}}
						multiline
						minRows={2}
						helperText={
							!isTextInputError ? null : (
								<Stack alignItems={'center'} direction={'row'}>
									<ExclamationMarkIconSmall />
									<Typography sx={{ ...refineFonts.smallRegular }}>
										{CheckQuestion.HintText || 'Please provide more details to submit'}
									</Typography>
								</Stack>
							)
						}
						label={
							openAnswer ? null : (
								<Typography sx={{ ...refineFonts.mediumRegular }} whiteSpace={'normal'}>
									{inputLabelText}
								</Typography>
							)
						}
						onChange={(e: { target: { value: string } }) => {
							props.setCurrentAnswer(selectedAnswerId, e.target.value);
							setOpenAnswer(e.target.value as string);
						}}
					/>
				</Stack>
			</Fade>
			<Box height={'2px'} id={'scrollTarget'} ref={scrollTargetRef} />
		</Stack>
	);
}
